import { Close } from "@mui/icons-material";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { memo, useMemo } from "react";
import {
  useMessageDialogHandlers,
  useMessageDialogState,
} from "../../App/State/Hooks/MessageDialog/useMessageDialog";
import { useAppSelector } from "../../App/State/Hooks/reduxHooks";
import { selectSelectedCandidatesByType } from "../../App/State/Slices/MessageDialogSlice";
import { IGroup } from "../../App/Typings/Groups";
import { IMessageContainers } from "../Enums/MessageEnums";
import { getMessageContainerDisplayName } from "../FunctionMethods/MessageFunctions";
import MessageHistoryTabs from "../SendGroupMessage/Components/HistoryTabs/MessageHistoryTabs";
import { MemoMessageDeliveryTabs } from "../SendGroupMessage/Components/MessageDeliveryTabs/MessageDeliveryTabs";

export default function MessageDialogGroupTabs({ group }: { group?: IGroup | undefined }) {
  const {
    id: groupId,
    campaignId,
    name: groupName,
  } = group ?? {
    name: "",
    id: "",
  };

  const { isGroupTabsOpen, dialogTitle, containerType } = useMessageDialogState();
  const selectedEmailCandidates = useAppSelector((state) => selectSelectedCandidatesByType(state, "email"));
  const selectedTextCandidates = useAppSelector((state) => selectSelectedCandidatesByType(state, "text"));
  const { handleCloseDialog } = useMessageDialogHandlers();
  const isEmail = IMessageContainers[containerType].includes("Email");
  const messageCandidates = useMemo(
    () => (isEmail ? selectedEmailCandidates : selectedTextCandidates),
    [isEmail, selectedEmailCandidates, selectedTextCandidates],
  );
  const messageDialogTitle = useMemo(
    () =>
      dialogTitle ||
      (group
        ? getMessageContainerDisplayName(IMessageContainers.GroupTextNow)
        : getMessageContainerDisplayName(containerType, messageCandidates)),
    [dialogTitle, group?.id, groupName, containerType, messageCandidates],
  );

  return (
    <Dialog
      id="message-dialog"
      aria-labelledby="sendgroupmessage-title"
      open={isGroupTabsOpen}
      onClose={handleCloseDialog}
      scroll="paper"
      fullWidth={true}
      maxWidth="lg"
      disableEnforceFocus={true}
      PaperProps={{
        sx: {
          maxHeight: { xs: "calc(100vh - 1rem)", sm: "calc(100vh - 64px)" },
          height: { xs: "100vh", sm: "90vh" },
          width: { xs: "calc(100% - 1rem)", sm: "calc(100% - 2rem)" },
          m: { xs: "1rem", sm: "2rem" },
        },
      }}
    >
      <DialogTitle
        id="sendgroupmessage-title"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 1,
          color: "white",
          backgroundColor: "#00af4a",
        }}
      >
        <Box>
          {messageDialogTitle}
          {group && !!groupName ? `: ${groupName}` : null}
        </Box>
        <Box>
          <IconButton
            color="inherit"
            onClick={handleCloseDialog}
            aria-label="Close Delivered Messages Window"
            aria-controls="delivered-message-window"
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers={true} sx={{ paddingBottom: 0 }}>
        <MemoMessageDeliveryTabs groupId={groupId} campaignId={campaignId || ""} />
        {groupId && <MessageHistoryTabs isGroup={true} groupId={groupId} />}
      </DialogContent>

      <DialogActions sx={{ py: 2, px: 3 }}>
        <Button
          style={{ marginTop: "20px" }}
          variant={"outlined"}
          color="primary"
          onClick={handleCloseDialog}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export const MemoMessageDialogGroupTabs = memo(MessageDialogGroupTabs);
